/**
 * Two-way between-subjects ANOVA with replication, balanced or not.
 *
 * Sums of squares are Type III — each effect adjusted for every other, including the
 * interaction — which is what SPSS reports by default and what R gives with
 * `car::Anova(type = 3)` under sum-to-zero contrasts. In a balanced design Type I, II
 * and III agree; in an unbalanced one they do not, and Type III is the one that tests
 * the hypotheses about unweighted marginal means a factorial experiment is usually
 * designed to test.
 *
 * Method: the cell-means model. With every cell non-empty, the hypothesis for an effect
 * is a set of contrasts L on the cell means, and its sum of squares is
 * SS = (Lμ̂)′ (L D⁻¹ L′)⁻¹ (Lμ̂), where D is the diagonal matrix of cell sizes. This is
 * algebraically the same as fitting the effect-coded regression with and without the
 * effect's columns, without forming the design matrix.
 */

import { fUpper } from "@/lib/stats/distributions";
import { dot, solveLinear } from "@/lib/tool-kit/stats-tests/linear";

export interface Observation {
  a: string;
  b: string;
  value: number;
}

export interface CellStats {
  n: number;
  mean: number;
  sd: number;
}

export type EffectKey = "a" | "b" | "ab";

export interface EffectRow {
  key: EffectKey;
  ss: number;
  df: number;
  ms: number;
  f: number;
  p: number;
  partialEtaSquared: number;
}

export interface TwoWayAnova {
  levelsA: string[];
  levelsB: string[];
  /** cells[i][j] for level i of A and level j of B. */
  cells: CellStats[][];
  /** Unweighted marginal means — the means Type III compares. */
  marginalA: number[];
  marginalB: number[];
  grandMean: number;
  n: number;
  balanced: boolean;
  effects: EffectRow[];
  ssError: number;
  dfError: number;
  msError: number;
  ssTotal: number;
  dfTotal: number;
}

export type TwoWayFailure =
  | "too-few-levels"
  | "too-many-levels"
  | "empty-cell"
  | "no-replication"
  | "no-variance"
  | "singular";

export type TwoWayOutcome =
  | { ok: true; result: TwoWayAnova }
  | { ok: false; reason: TwoWayFailure; detail?: string };

export const MAX_LEVELS = 8;

function levelsOf(observations: readonly Observation[], key: "a" | "b") {
  const seen: string[] = [];
  for (const observation of observations) {
    if (!seen.includes(observation[key])) {
      seen.push(observation[key]);
    }
  }
  return seen;
}

function cellStats(values: number[]): CellStats {
  const n = values.length;
  const m = values.reduce((acc, value) => acc + value, 0) / n;
  const ss = values.reduce((acc, value) => acc + (value - m) ** 2, 0);
  return { n, mean: m, sd: n > 1 ? Math.sqrt(ss / (n - 1)) : Number.NaN };
}

/** Rows of L for the A main effect: each level's unweighted mean against the last. */
function mainEffectContrasts(
  levels: number,
  other: number,
  cellIndex: (main: number, other: number) => number
): number[][] {
  const rows: number[][] = [];
  for (let r = 0; r < levels - 1; r++) {
    const row = new Array<number>(levels * other).fill(0);
    for (let j = 0; j < other; j++) {
      row[cellIndex(r, j)] += 1 / other;
      row[cellIndex(levels - 1, j)] -= 1 / other;
    }
    rows.push(row);
  }
  return rows;
}

function interactionContrasts(a: number, b: number): number[][] {
  const rows: number[][] = [];
  for (let r = 0; r < a - 1; r++) {
    for (let s = 0; s < b - 1; s++) {
      const row = new Array<number>(a * b).fill(0);
      row[r * b + s] += 1;
      row[r * b + (b - 1)] -= 1;
      row[(a - 1) * b + s] -= 1;
      row[(a - 1) * b + (b - 1)] += 1;
      rows.push(row);
    }
  }
  return rows;
}

/** (Lμ̂)′ (L D⁻¹ L′)⁻¹ (Lμ̂), or null if the contrast matrix is singular. */
function hypothesisSs(
  contrasts: number[][],
  means: number[],
  sizes: number[]
): number | null {
  const estimates = contrasts.map((row) => dot(row, means));
  const m = contrasts.map((ri) =>
    contrasts.map((rj) =>
      ri.reduce((acc, value, c) => acc + (value * rj[c]) / sizes[c], 0)
    )
  );
  const solution = solveLinear(m, estimates);
  return solution === null ? null : dot(estimates, solution);
}

function groupValues(
  observations: readonly Observation[],
  levelsA: string[],
  levelsB: string[]
): number[][][] {
  const values = levelsA.map(() => levelsB.map(() => [] as number[]));
  for (const observation of observations) {
    values[levelsA.indexOf(observation.a)][levelsB.indexOf(observation.b)].push(
      observation.value
    );
  }
  return values;
}

function checkDesign(
  levelsA: string[],
  levelsB: string[],
  values: number[][][],
  n: number
): TwoWayOutcome | null {
  if (levelsA.length < 2 || levelsB.length < 2) {
    return { ok: false, reason: "too-few-levels" };
  }
  if (levelsA.length > MAX_LEVELS || levelsB.length > MAX_LEVELS) {
    return { ok: false, reason: "too-many-levels" };
  }
  for (const [i, row] of values.entries()) {
    for (const [j, cell] of row.entries()) {
      if (cell.length === 0) {
        return {
          ok: false,
          reason: "empty-cell",
          detail: `${levelsA[i]} × ${levelsB[j]}`,
        };
      }
    }
  }
  if (n <= levelsA.length * levelsB.length) {
    return { ok: false, reason: "no-replication" };
  }
  return null;
}

function effectRow(
  key: EffectKey,
  ss: number,
  df: number,
  msError: number,
  dfError: number,
  ssError: number
): EffectRow {
  const ms = ss / df;
  const f = ms / msError;
  return {
    key,
    ss,
    df,
    ms,
    f,
    p: fUpper(f, df, dfError),
    partialEtaSquared: ss / (ss + ssError),
  };
}

export function twoWayAnova(
  observations: readonly Observation[]
): TwoWayOutcome {
  const levelsA = levelsOf(observations, "a");
  const levelsB = levelsOf(observations, "b");
  const values = groupValues(observations, levelsA, levelsB);
  const n = observations.length;
  const failure = checkDesign(levelsA, levelsB, values, n);
  if (failure) {
    return failure;
  }
  const a = levelsA.length;
  const b = levelsB.length;
  const cells = values.map((row) => row.map(cellStats));
  const means = cells.flat().map((cell) => cell.mean);
  const sizes = cells.flat().map((cell) => cell.n);
  const all = observations.map((observation) => observation.value);
  const grandMean = all.reduce((acc, value) => acc + value, 0) / n;
  const ssTotal = all.reduce((acc, value) => acc + (value - grandMean) ** 2, 0);
  let ssError = 0;
  for (const [i, row] of values.entries()) {
    for (const [j, cell] of row.entries()) {
      for (const value of cell) {
        ssError += (value - cells[i][j].mean) ** 2;
      }
    }
  }
  const dfError = n - a * b;
  if (ssError <= 1e-12 * Math.max(1, ssTotal)) {
    return { ok: false, reason: "no-variance" };
  }
  const msError = ssError / dfError;
  const ssA = hypothesisSs(
    mainEffectContrasts(a, b, (i, j) => i * b + j),
    means,
    sizes
  );
  const ssB = hypothesisSs(
    mainEffectContrasts(b, a, (j, i) => i * b + j),
    means,
    sizes
  );
  const ssAb = hypothesisSs(interactionContrasts(a, b), means, sizes);
  if (ssA === null || ssB === null || ssAb === null) {
    return { ok: false, reason: "singular" };
  }
  return {
    ok: true,
    result: {
      levelsA,
      levelsB,
      cells,
      marginalA: cells.map(
        (row) => row.reduce((acc, cell) => acc + cell.mean, 0) / b
      ),
      marginalB: levelsB.map(
        (_, j) => cells.reduce((acc, row) => acc + row[j].mean, 0) / a
      ),
      grandMean,
      n,
      balanced: sizes.every((size) => size === sizes[0]),
      effects: [
        effectRow("a", ssA, a - 1, msError, dfError, ssError),
        effectRow("b", ssB, b - 1, msError, dfError, ssError),
        effectRow("ab", ssAb, (a - 1) * (b - 1), msError, dfError, ssError),
      ],
      ssError,
      dfError,
      msError,
      ssTotal,
      dfTotal: n - 1,
    },
  };
}

const LINE_BREAK = /\r?\n/;
const TAB = "\t";
const WHITESPACE_RUN = /\s+/;

function splitLine(line: string): string[] {
  if (line.includes(TAB)) {
    return line.split(TAB);
  }
  if (line.includes(";")) {
    return line.split(";");
  }
  if (line.includes(",")) {
    return line.split(",");
  }
  return line.split(WHITESPACE_RUN);
}

export interface ParsedLongTable {
  observations: Observation[];
  /** Non-empty lines that were not "level, level, number" — usually a header. */
  skipped: number;
}

/**
 * Read "A level, B level, value" lines.
 *
 * Fields are split on tabs, else semicolons, else commas, else spaces — so a decimal
 * comma works when the fields are separated by tabs or semicolons. `parseValue` is the
 * locale-tolerant number parser, injected so this module stays dependency-free.
 */
export function parseLongTable(
  text: string,
  parseValue: (raw: string) => number | null
): ParsedLongTable {
  const observations: Observation[] = [];
  let skipped = 0;
  for (const raw of text.split(LINE_BREAK)) {
    const line = raw.trim();
    if (line === "") {
      continue;
    }
    const fields = splitLine(line)
      .map((field) => field.trim())
      .filter((field) => field !== "");
    const value = fields.length === 3 ? parseValue(fields[2]) : null;
    if (value === null) {
      skipped += 1;
      continue;
    }
    observations.push({ a: fields[0], b: fields[1], value });
  }
  return { observations, skipped };
}
